import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import truncate from "truncate-html";
import axios from "axios";
import "../../../assets/css/PublicAnnounceSection.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function formatDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;

  return {
    month: date.toLocaleString("en", { month: "short" }).toUpperCase(),
    day: String(date.getDate()).padStart(2, "0"),
    year: date.getFullYear(),
    iso: date.toISOString().slice(0, 10),
  };
}

const TruncateHtml = (htmlString, length) =>
  truncate(htmlString || "", length, { byWords: true });

function hasImage(value) {
  return (
    typeof value === "string" &&
    value.trim() !== "" &&
    value !== "undefined" &&
    value !== "null"
  );
}

function AnnouncementImage({ item }) {
  if (!hasImage(item?.passport)) {
    return (
      // <div className="fmw-announcement-image-placeholder">
      //   <i className="fa-regular fa-newspaper" aria-hidden="true" />
      //   <span>FMW ANNOUNCEMENT</span>
      // </div>
      <></>
    );
  }

  return (
    <img
      src={`${BACKEND_URL}upload/${item.passport}`}
      alt={item.title || "Ministry announcement"}
      className="fmw-announcement-image"
      loading="lazy"
      decoding="async"
    />
  );
}

function AnnouncementDate({ value }) {
  const date = formatDate(value);
  if (!date) return null;
  return (
    <time className="fmw-announcement-date" dateTime={date.iso}>
      <span>{date.month}</span>
      <strong>{date.day}</strong>
      <span>{date.year}</span>
    </time>
  );
}

function PaginationControls({ pagination, onPageChange, loading, label }) {
  const current = Number(pagination?.current_page || 1);
  const last = Math.max(1, Number(pagination?.last_page || 1));

  const go = (next) => onPageChange(Math.min(last, Math.max(1, next)));

  return (
    <nav className="fmw-announcement-pagination" aria-label={label}>
      <span className="fmw-announcement-page-status">
        Page <strong>{current}</strong> of {last}
      </span>
      <div className="fmw-announcement-page-actions">
        <button
          type="button"
          onClick={() => go(1)}
          disabled={current <= 1 || loading}
          aria-label="First page"
        >
          «
        </button>
        <button
          type="button"
          onClick={() => go(current - 1)}
          disabled={current <= 1 || loading}
          aria-label="Previous page"
        >
          ‹
        </button>
        <button
          type="button"
          onClick={() => go(current + 1)}
          disabled={current >= last || loading}
          aria-label="Next page"
        >
          ›
        </button>
        <button
          type="button"
          onClick={() => go(last)}
          disabled={current >= last || loading}
          aria-label="Last page"
        >
          »
        </button>
      </div>
    </nav>
  );
}

function PublicAnnounceSection({
  datacontent = [],
  initialDownloadData = [],
  initialPagination,
  id,
}) {
  const [newsList, setNewsList] = useState(datacontent || []);
  const [newsDownloadList, setNewsDownloadList] = useState(
    initialDownloadData || [],
  );
  const [page, setPage] = useState(initialPagination?.current_page || 1);
  const [pagination, setPagination] = useState(
    initialPagination || { current_page: 1, last_page: 1 },
  );
  const [pageDownload, setPageDownload] = useState(1);
  const [paginationDownload, setPaginationDownload] = useState(
    initialPagination || { current_page: 1, last_page: 1 },
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const headerTitle =
    datacontent?.[0]?.title?.length > 0
      ? datacontent[0].title
      : "Announcements";

  useEffect(() => {
    setNewsList(datacontent || []);
    setNewsDownloadList(initialDownloadData || []);
    if (initialPagination) {
      setPagination(initialPagination);
      setPage(initialPagination.current_page || 1);
    }
  }, [datacontent, initialDownloadData, initialPagination]);

  useEffect(() => {
    let active = true;

    async function fetchNews() {
      setLoading(true);
      setError(false);

      try {
        const responses = await Promise.all([
          axios.get(
            `${BASE_URL}posdata?pageName[eq]=${encodeURIComponent(id ?? "")}&columes[eq]=title_show_echo_01&ministry[eq]=0&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&display01[eq]=1&page=${page}&per_page=1`,
          ),
          axios.get(
            `${BASE_URL}posdata?columes[eq]=title_show_echo_01wa&ministry[eq]=0&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&page=${pageDownload}&per_page=7`,
          ),
        ]);

        if (!active) return;
        const announcementResponse = responses[0];
        const downloadResponse = responses[1];

        setNewsList(announcementResponse?.data?.allData || []);
        setNewsDownloadList(downloadResponse?.data?.allData || []);
        setPagination(
          announcementResponse?.data?.pagination || {
            current_page: page,
            last_page: 1,
          },
        );
        setPaginationDownload(
          downloadResponse?.data?.pagination || {
            current_page: pageDownload,
            last_page: 1,
          },
        );
      } catch (err) {
        if (active) {
          setError(true);
          console.error("Unable to fetch FMW announcements:", err);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchNews();
    return () => {
      active = false;
    };
  }, [page, pageDownload, id, retryKey]);

  const alignment = newsList?.[0]?.aligment009?.toLowerCase();
  const showDownloads =
    datacontent?.[0]?.aligment009?.toLowerCase() === "center";

  return (
    <section className="fmw-public-announcements" id="portfolio-details">
      <div className="container">
        <header className="fmw-announcement-heading">
          <div>
            <span className="fmw-announcement-kicker">
              OFFICIAL NOTICES · PUBLIC INFORMATION
            </span>
            <h2>{headerTitle}</h2>
          </div>
          <p className="fmw-journal-section-label">
            <i className="fa-solid fa-bullhorn" aria-hidden="true"></i>
          </p>
        </header>

        {loading && (
          <div
            className="fmw-announcement-status"
            role="status"
            aria-live="polite"
          >
            <span className="fmw-announcement-spinner" />
            Updating announcements…
          </div>
        )}

        {error && newsList.length === 0 && (
          <div className="fmw-announcement-empty" role="alert">
            <h3>Announcements are temporarily unavailable.</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              onClick={() => setRetryKey((value) => value + 1)}
            >
              Retry loading
            </button>
          </div>
        )}

        {!loading && !error && newsList.length === 0 && (
          <div className="fmw-announcement-empty">
            <h3>No announcements available yet.</h3>
            <p>Published notices will appear here when they are available.</p>
          </div>
        )}

        {newsList.length > 0 && (
          <div
            className={`fmw-announcement-layout ${showDownloads ? "has-downloads" : ""} ${alignment === "center" ? "is-centered" : ""}`}
          >
            <div className="fmw-announcement-main">
              {newsList.map((item, index) => {
                const itemAlignment = item?.aligment009?.toLowerCase();
                const isLeft = itemAlignment === "left";
                const isRight = itemAlignment === "right";
                const isCenter = itemAlignment === "center";

                return (
                  <article
                    className={`fmw-announcement-item is-${itemAlignment || "left"}`}
                    key={item.id || index}
                  >
                    {(isLeft || isRight) && (
                      <div
                        className={`fmw-announcement-split ${isRight ? "image-right" : ""}`}
                      >
                        <div className="fmw-announcement-visual">
                          <AnnouncementImage item={item} />
                          <AnnouncementDate value={item.daterqw} />
                        </div>
                        <div className="fmw-announcement-copy">
                          {item.caption && (
                            <span className="fmw-announcement-caption">
                              <HTMLDisplay html={item.caption} type="title" />
                            </span>
                          )}
                          <h3>
                            <HTMLDisplay html={item.story01} type="title" />
                          </h3>
                          <div className="fmw-announcement-body">
                            <HTMLDisplay html={item.story01} type="body" />
                          </div>
                          {item.textSize === "0px" && (
                            <Link
                              to={`/read/${item.id}`}
                              className="fmw-announcement-read"
                            >
                              Read full announcement{" "}
                              <span aria-hidden="true">↗</span>
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                    {isCenter && (
                      <div className="fmw-announcement-centered">
                        <div className="fmw-announcement-centered-visual">
                          <AnnouncementImage item={item} />
                          <AnnouncementDate value={item.daterqw} />
                        </div>
                        <div className="fmw-announcement-copy">
                          {item.caption && (
                            <span className="fmw-announcement-caption">
                              <HTMLDisplay html={item.caption} type="title" />
                            </span>
                          )}
                          <h3>
                            <HTMLDisplay html={item.story01} type="title" />
                          </h3>
                          <div className="fmw-announcement-body">
                            <HTMLDisplay html={item.story01} type="body" />
                          </div>
                          {item.textSize === "0px" && (
                            <Link
                              to={`/read/${item.id}`}
                              className="fmw-announcement-read"
                            >
                              Read full announcement{" "}
                              <span aria-hidden="true">↗</span>
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}

              <PaginationControls
                pagination={pagination}
                onPageChange={setPage}
                loading={loading}
                label="Announcements pagination"
              />
            </div>

            {showDownloads && (
              <aside
                className="fmw-announcement-downloads"
                aria-label="Downloadable resources"
              >
                <div className="fmw-downloads-heading">
                  <span className="fmw-downloads-icon">
                    <i className="fa-solid fa-folder-open" aria-hidden="true" />
                  </span>
                  <div>
                    <span>DOCUMENT CENTRE</span>
                    <h3>Downloadable Resources</h3>
                  </div>
                </div>
                <p className="fmw-downloads-intro">
                  Access official documents and published resources.
                </p>

                {newsDownloadList.length === 0 ? (
                  <p className="fmw-downloads-empty">
                    No documents are available on this page yet.
                  </p>
                ) : (
                  <ul className="fmw-download-list">
                    {newsDownloadList.map((file, index) => (
                      <li key={file.id || file.addressLink || index}>
                        <span className="fmw-download-file-icon">
                          <i
                            className="fa-regular fa-file-pdf"
                            aria-hidden="true"
                          />
                        </span>
                        <Link
                          to={`${BACKEND_URL}upload/${file.addressLink}`}
                          download
                          title={file.title || "Download document"}
                          target="_blank"
                        >
                          {TruncateHtml(file.title, 12)}
                        </Link>
                        <i
                          className="fa-solid fa-download fmw-download-action"
                          aria-hidden="true"
                        />
                      </li>
                    ))}
                  </ul>
                )}

                <PaginationControls
                  pagination={paginationDownload}
                  onPageChange={setPageDownload}
                  loading={loading}
                  label="Downloadable resources pagination"
                />
              </aside>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default React.memo(PublicAnnounceSection);
